import shippingData from "./data.json";

/**
 * Parse a postcode range string like "2000-2079,2085-2107,2158"
 * into an array of [start, end] pairs.
 */
function parsePostcodeRanges(rangeStr) {
  return rangeStr.split(",").map((part) => {
    const trimmed = part.trim();
    if (trimmed.includes("-")) {
      const [start, end] = trimmed.split("-").map(Number);
      return [start, end];
    }
    const num = Number(trimmed);
    return [num, num];
  });
}

/**
 * Check if a postcode falls within any of the parsed ranges.
 */
function isInRanges(postcode, ranges) {
  return ranges.some(([start, end]) => postcode >= start && postcode <= end);
}

/**
 * Look up the zone name for a given postcode.
 * Returns the zone string (e.g. "Near State Metro") or null if not found.
 */
function getZone(postcode) {
  const pc = Number(postcode);
  for (const [, info] of Object.entries(shippingData.zones)) {
    const ranges = parsePostcodeRanges(info.postcodes);
    if (isInRanges(pc, ranges)) {
      return info.zone;
    }
  }
  return null;
}

/**
 * Calculate shipping cost for a single parcel.
 *
 * Excel formula (from AUpost.xlsx 数据表!L column, 2026 update):
 * =IF(AND(长<=100, 重量<=22, 长*宽*高/4000<=62),
 *   IF(B5<=0.5, E3*(1+fuel),    -- E3 = VLOOKUP(zone, 价卡!B:J, 3) = 251g to 500g
 *   IF(B5<=1, F3*(1+fuel),      -- F3 = VLOOKUP(zone, 价卡!B:J, 4) = 501g to 1kg
 *   IF(B5<=3, G3*(1+fuel),      -- G3 = VLOOKUP(zone, 价卡!B:J, 5) = 1.01kg to 3kg
 *   IF(B5<=5, H3*(1+fuel),      -- H3 = VLOOKUP(zone, 价卡!B:J, 6) = 3.01kg to 5kg
 *   (I3+J3+CEILING(MAX(重量,长*宽*高/4000),1)*K3)*(1+fuel)
 *                               -- I3 = Basic, J3 = Subsequent, K3 = Per Kg
 *   )))), "")
 *
 * 2026 变更: VLOOKUP 从价卡 col3 开始，跳过了 "Up to 250g" 列。
 *   ≤0.5kg 现在使用 "251g to 500g" 费率
 *   ≤1kg   使用 "501g to 1kg" 费率
 *   ≤3kg   使用 "1.01kg to 3kg" 费率
 *   ≤5kg   使用 "3.01kg to 5kg" 费率
 *   >5kg   = Basic + Subsequent + CEIL(billableWeight) × Per Kg
 *
 * 不发区 (excluded postcodes) 不产生运费预估，直接返回错误。
 *
 * @param {number|string} postcode    - Australian destination postcode
 * @param {number}        weightKg    - Parcel actual weight in kilograms
 * @param {number}        lengthCm    - Parcel length in cm
 * @param {number}        widthCm     - Parcel width in cm
 * @param {number}        heightCm    - Parcel height in cm
 * @param {number}        [markup=0]  - Fuel surcharge rate (e.g. 0.123 for 12.3%)
 * @returns {{ cost: number, zone: string, destination: string } | { error: string }}
 */
export function calculateShipping(postcode, weightKg, lengthCm, widthCm, heightCm, markup = 0) {
  const pc = Number(postcode);

  // 0. 不发区检查 — 对应 AUpost.xlsx "不发区" sheet 的邮编列表
  //    这些邮编不产生运费预估，直接排除
  if (shippingData.excludedPostcodes.includes(pc)) {
    return { error: "EXCLUDED", message: `Postcode ${pc} is in the excluded (no-delivery) list.` };
  }

  // 1. 尺寸/重量验证: AND(长<=100, 重量<=22, 长*宽*高/4000<=62)
  const volWeight = (lengthCm * widthCm * heightCm) / 4000;
  if (lengthCm > 100 || weightKg > 22 || volWeight > 62) {
    return { error: "OVER_LIMIT", message: `Parcel exceeds size limits (L=${lengthCm}, W=${widthCm}, H=${heightCm}, Wt=${weightKg}).` };
  }

  // 2. 邮编 -> 分区
  let zone = null;
  let destination = null;
  for (const [dest, info] of Object.entries(shippingData.zones)) {
    const ranges = parsePostcodeRanges(info.postcodes);
    if (isInRanges(pc, ranges)) {
      zone = info.zone;
      destination = dest;
      break;
    }
  }

  if (!zone) {
    return { error: "UNKNOWN_POSTCODE", message: `Postcode ${pc} not found in any zone.` };
  }

  // 3. 获取分区费率
  const rates = shippingData.pricing[zone];
  if (!rates) {
    return { error: "NO_PRICING", message: `No pricing found for zone "${zone}".` };
  }

  // 4. 计费重量 = MAX(实际重量, 体积重量)
  //    ≤5kg 档位: 不取整，直接比较
  //    >5kg: CEILING(billableWeight, 1) 向上取整
  const billableWeight = Math.max(weightKg, volWeight);
  let cost;

  if (billableWeight <= 0.5) {
    // E3 = VLOOKUP col3 = 价卡 "251g to 500g" (用于 ≤0.5kg)
    cost = rates["251gTo500g"];
  } else if (billableWeight <= 1) {
    // F3 = VLOOKUP col4 = 价卡 "501g to 1kg" (用于 ≤1kg)
    cost = rates["501gTo1kg"];
  } else if (billableWeight <= 3) {
    // G3 = VLOOKUP col5 = 价卡 "1.01kg to 3kg" (用于 ≤3kg)
    cost = rates["1_01kgTo3kg"];
  } else if (billableWeight <= 5) {
    // H3 = VLOOKUP col6 = 价卡 "3.01kg to 5kg" (用于 ≤5kg)
    cost = rates["3_01kgTo5kg"];
  } else {
    // >5kg: (I3 + J3 + CEILING(billableWeight,1) * K3)
    //   I3 = Basic, J3 = Subsequent, K3 = Per Kg
    const { basic, subsequent, perKg } = rates.over5kg;
    const ceilWeight = Math.ceil(billableWeight);
    cost = basic + subsequent + ceilWeight * perKg;
  }

  // 5. 燃油附加费: cost * (1 + fuel levy)
  cost = cost * (1 + markup);

  return {
    postcode: pc,
    destination,
    zone,
    weightKg,
    billableWeight: +billableWeight.toFixed(4),
    cost: Math.round(cost * 10000) / 10000,
  };
}

/*
使用说明
1. calculateShipping(postcode, weightKg, lengthCm, widthCm, heightCm, [markup])
2. 参数说明:
   - postcode: 邮编 (必填)
   - weightKg: 实际重量 (kg)
   - lengthCm: 长度 (cm)
   - widthCm: 宽度 (cm)
   - heightCm: 高度 (cm)
   - markup: 燃油附加费率 (e.g. 0.123 = 12.3%, 默认 0)
3. 不发区 (excludedPostcodes): 138 个邮编无法配送，直接返回错误
4. 尺寸/重量限制 (超出返回错误):
   - 长度 ≤ 100cm
   - 重量 ≤ 22kg
   - 体积重量 (L×W×H/4000) ≤ 62
5. 计费重量: MAX(实际重量, 体积重量)
6. 运费公式 (2026 更新 — 去掉了 "Up to 250g" 档位，VLOOKUP从价卡col3开始):
   - ≤0.5kg: 251gTo500g rate × (1 + fuel)
   - ≤1kg: 501gTo1kg rate × (1 + fuel)
   - ≤3kg: 1_01kgTo3kg rate × (1 + fuel)
   - ≤5kg: 3_01kgTo5kg rate × (1 + fuel)
   - >5kg: (basic + subsequent + CEIL(billableWeight) × perKg) × (1 + fuel)
7. 返回值:
   - 成功: { postcode, destination, zone, weightKg, billableWeight, cost }
   - 失败: { error: "EXCLUDED"|"OVER_LIMIT"|"UNKNOWN_POSTCODE"|"NO_PRICING", message }
*/
